import { action, computed, makeObservable, observable } from 'mobx';
import { runRequest, safeRunRequest } from 'utils/networkRequests';

import {
  IDocumentResponse,
  IListDocumentsResponse,
  IListOrdersResponse,
  IOrder,
  IOrderResponse,
  ISummary,
  ITradeResponse,
  OrderStatus,
  OrderType
} from './networkInterfaces';

class InvestmentStore {
  investmentData: ISummary = {};

  constructor() {
    makeObservable(this, {
      investmentData: observable,
      setInvestmentData: action,
      getInvestmentData: computed,

      listOrders: action,

      getSummary: action,

      getOrder: action,

      startIntBuyOrder: action,
      startBidBuyOrder: action,
      startBidSellOrder: action,

      cancelOrder: action,
      approveOrder: action,

      listDocuments: action,
      getDocument: action,
      deleteDocument: action
    });
  }

  setInvestmentData(data: ISummary) {
    this.investmentData = data;
  }

  getSummary(body: { [type: string]: { created_at?: number } } = {}, type?: string) {
    return safeRunRequest('summary', body).then((response) => {
      const data = response?.data;

      switch (type) {
        case 'summary_filter':
          this.setInvestmentData({ ...this.investmentData, summary: data.summary });
          return this.investmentData;
        case 'dividends_filter':
          this.setInvestmentData({ ...this.investmentData, dividends: data.dividends });
          return this.investmentData;
        default:
          this.setInvestmentData(data);
          return this.investmentData;
      }
    });
  }

  listOrders(offeringId: string, orderType?: number[], orderStatus?: number[]) {
    const body = {
      offering_id: offeringId,
      ...{ order_type: orderType },
      ...{ order_status: orderStatus }
    };

    return safeRunRequest('listOrders', body).then((response) =>
      this.handleOrderListResponse(response)
    );
  }

  getActiveOrder(offeringId: string, orderType: OrderType) {
    return this.listOrders(
      offeringId,
      [orderType],
      [OrderStatus.pending, OrderStatus.matched, OrderStatus.active, OrderStatus.executing]
    ).then((response) => {
      const orders = response?.data;
      let currentOrder: IOrder | undefined;

      if (orderType === OrderType.bid_sell) {
        if (orders && orders.length !== 0) {
          currentOrder = orders.reduce((prev, current) =>
            prev.price && current.price && prev.price < current.price ? prev : current
          );
        }
      } else {
        if (orders && orders.length !== 0) {
          currentOrder = orders.reduce((prev, current) =>
            prev.price && current.price && prev?.price > current.price ? prev : current
          );
        }
      }

      return currentOrder;
    });
  }

  getOrder(orderId?: string) {
    const body = {
      id: orderId
    };

    return safeRunRequest('getOrder', body).then((response) => this.handleOrderResponse(response));
  }

  startIntBuyOrder({
    offeringId,
    total,
    orderId,
    notifyUser
  }: {
    offeringId: string;
    total?: number;
    orderId?: string;
    notifyUser?: boolean;
  }) {
    const body = {
      order: {
        type: 'Order',
        attributes: {
          ...(orderId && { id: orderId }),
          offering_id: offeringId,
          total: total,
          investor_silent: !notifyUser
        }
      }
    };

    return runRequest('startIntBuyOrder', body).then((response) =>
      this.handleOrderResponse(response)
    );
  }

  startBidBuyOrder({
    offeringId,
    quantity,
    price,
    total,
    orderId,
    notifyUser
  }: {
    offeringId: string;
    quantity?: number;
    price?: number;
    total?: number;
    orderId?: string;
    notifyUser?: boolean;
  }) {
    const body = {
      order: {
        type: 'Order',
        attributes: {
          ...(orderId && { id: orderId }),
          offering_id: offeringId,
          quantity: quantity,
          price: price,
          total: total,
          investor_silent: !notifyUser
        }
      }
    };

    return runRequest('startBidBuyOrder', body).then((response) =>
      this.handleOrderResponse(response)
    );
  }

  startBidSellOrder({
    offeringId,
    quantity,
    price,
    total,
    orderId,
    notifyUser
  }: {
    offeringId: string;
    quantity?: number;
    price?: number;
    total?: number;
    orderId?: string;
    notifyUser?: boolean;
  }) {
    const body = {
      order: {
        type: 'Order',
        attributes: {
          ...(orderId && { id: orderId }),
          offering_id: offeringId,
          quantity: quantity,
          price: price,
          total: total,
          investor_silent: !notifyUser
        }
      }
    };

    return runRequest('startBidSellOrder', body).then((response) =>
      this.handleOrderResponse(response)
    );
  }

  cancelOrder({
    offeringId,
    orderId,
    notifyUser
  }: {
    offeringId: string;
    orderId: string;
    notifyUser?: boolean;
  }) {
    const body = {
      offering_id: offeringId,
      id: orderId,
      investor_silent: !notifyUser
    };
    return runRequest('cancelOrder', body).then((response) => this.handleOrderResponse(response));
  }

  approveOrder({
    offeringId,
    orderId,
    approvalId,
    notifyUser
  }: {
    offeringId: string;
    orderId: string;
    approvalId?: string;
    notifyUser?: boolean;
  }) {
    const body = {
      id: orderId,
      offering_id: offeringId,
      approval_id: approvalId,
      investor_silent: !notifyUser
    };

    return safeRunRequest('approveOrder', body).then((response) =>
      this.handleOrderResponse(response)
    );
  }

  listDocuments(orderId: string) {
    const body = {
      order_id: orderId
    };

    return safeRunRequest('listDocuments', body).then((response) =>
      this.handleDocumentListResponse(response)
    );
  }

  getDocument(documentId: string, callbackUrl?: string) {
    const body = {
      id: documentId,
      callback_url: callbackUrl
    };

    return safeRunRequest('getDocument', body).then((response) =>
      this.handleDocumentResponse(response)
    );
  }

  deleteDocument(documentId: string) {
    const body = {
      id: documentId
    };

    return safeRunRequest('deleteDocument', body).then((response) =>
      this.handleDocumentResponse(response)
    );
  }

  handleOrderResponse(response: IOrderResponse) {
    return response?.data?.attributes;
  }

  handleTradeResponse(response: ITradeResponse) {
    return response?.data?.attributes;
  }

  handleOrderListResponse(response: IListOrdersResponse) {
    return {
      total: response?.total,
      data: response?.data
        ? response?.data?.map((order) => {
            return { ...order.attributes };
          })
        : []
    };
  }

  get getInvestmentData() {
    return this.investmentData;
  }

  handleDocumentResponse(response: IDocumentResponse) {
    return response?.data?.attributes;
  }

  handleDocumentListResponse(response: IListDocumentsResponse) {
    return {
      total: response?.total,
      data: response?.data
        ? response?.data?.map((document) => {
            return { ...document.attributes };
          })
        : []
    };
  }
}

export default InvestmentStore;
